/**
 * dailyChallenge.js
 *
 * Streak rule (enforced server-side via RPCs):
 * - Played within last 72 hours (today, 1 day ago, or 2 days ago) and passed: streak +1
 * - Last play 3+ days ago and passed: reset to 1
 * - Failed (score < 3): reset to 0
 * - Already played today: no change (idempotent)
 */

import { supabase } from './supabase';

const DAILY_KEY = 'kyg_daily';
const NBA_DAILY_KEY = 'kyg_nba_daily';
const MOSH_DAILY_KEY = 'kyg_mosh_daily';
const NTG_DAILY_KEY = 'kyg_ntg';
const PASS_THRESHOLD = 3;
const NTG_PASS_THRESHOLD = 1;

// Daily reset boundary. Every daily "today" computation (played-today, daily
// selection, NTG question, save/claim) shares this ONE zone so they never desync.
export const DAILY_TZ = 'America/Los_Angeles'; // midnight Pacific

// ── Guest-claim safety ──────────────────────────────────────────────────────
// A guest result lives in localStorage, which is per-BROWSER, not per-user.
// Without a marker, the claim runs again on EVERY 'SIGNED_IN' event (supabase-js
// emits it on initial session AND on every token refresh), and worse: if a
// DIFFERENT account signs in on the same browser it inherits the previous
// player's result. On a shared/switched browser that silently transfers a win
// (and a streak) to the wrong account.
//
// Stamping claimedBy fixes both: repeat events short-circuit, and a second
// account can never re-claim. We deliberately KEEP the record rather than
// clearing it, because hasPlayed*Today() reads lastPlayedDate to block a replay
// of today's game -- clearing would let the player play the same day twice.
function alreadyClaimed(data) {
  return !!data?.claimedBy;
}

function markClaimed(key, data, userId, extra) {
  try {
    localStorage.setItem(key, JSON.stringify({ ...data, ...(extra || {}), claimedBy: userId }));
  } catch {}
}

// A second, narrower hole the claimedBy marker can't close on its own: two
// SIGNED_IN deliveries can land nearly simultaneously (supabase-js emits more
// than one around a boot or an OAuth landing), and BOTH pass alreadyClaimed()
// before either RPC returns and writes the marker. The ntg_sr_results audit
// log caught exactly this in production: paired guest_claim rows ~20ms apart
// (frankg2 2026-08-01, andrwwcottler 2026-07-31). Streaks survive because the
// RPCs are idempotent per day; the latch exists so the audit log stays
// trustworthy. It is in-flight only, released when the claim settles: a FAILED
// claim can still retry on a later SIGNED_IN exactly as before, and a
// successful one is covered by claimedBy from then on. The check-and-add is
// synchronous (before the first await), so concurrent callers can't interleave.
const claimInFlight = new Set();
function withClaimLatch(key, fn) {
  return async function (user) {
    if (claimInFlight.has(key)) return; // concurrent duplicate event: first caller wins
    claimInFlight.add(key);
    try { return await fn(user); }
    finally { claimInFlight.delete(key); }
  };
}

export function getTodaysDate() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DAILY_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export function getChicagoDate() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DAILY_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// ───────────────────────────────────────────────────────────────────
// NFL DAILY
// ───────────────────────────────────────────────────────────────────

export function getDailyData() {
  try {
    const raw = localStorage.getItem(DAILY_KEY);
    if (!raw) return { streak: 0, lastPlayedDate: null, lastScore: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, lastPlayedDate: null, lastScore: null };
  }
}

export function hasPlayedToday() {
  return getDailyData().lastPlayedDate === getTodaysDate();
}

export function saveDailyProgress(answers, currentQuestion, score) {
  try {
    const data = getDailyData();
    localStorage.setItem(DAILY_KEY, JSON.stringify({
      ...data,
      inProgress: true,
      savedAnswers: answers,
      savedQuestion: currentQuestion,
      savedScore: score,
    }));
  } catch {}
}

// Per-sport exit counters for the daily leave-confirm.
//
// Fixes two bugs in the old single-counter version, which read AND wrote
// data.exits on kyg_daily for all three dailies:
//   1. Cross-sport bleed - exiting NBA or Mish Mosh burned the NFL free exit.
//      That matters here in a way it doesn't on Sr., because the second exit
//      carries a real penalty (locks in the score, or wipes progress).
//   2. It never reset. Nothing cleared data.exits - not clearDailyProgress,
//      not saveDailyResult - so "one free exit per day" was really "one free
//      exit, ever". Day two onwards, your FIRST exit got the harsh prompt.
// Scoped per sport and per Pacific day, matching the Sr. pattern. A legacy
// exits value with no exitsDate reads as 0, so stuck counters clear themselves.
const EXIT_KEYS = { nfl: DAILY_KEY, nba: NBA_DAILY_KEY, mosh: MOSH_DAILY_KEY };

function readDailyBlob(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function getDailyExits(sport) {
  try {
    const data = readDailyBlob(EXIT_KEYS[sport] || DAILY_KEY);
    if (data.exitsDate !== getTodaysDate()) return 0;
    return data.exits || 0;
  } catch {
    return 0;
  }
}

export function bumpDailyExits(sport) {
  try {
    const key = EXIT_KEYS[sport] || DAILY_KEY;
    const data = readDailyBlob(key);
    const prior = data.exitsDate === getTodaysDate() ? data.exits || 0 : 0;
    localStorage.setItem(key, JSON.stringify({
      ...data,
      exits: prior + 1,
      exitsDate: getTodaysDate(),
    }));
  } catch {}
}

export function getDailyProgress() {
  try {
    const data = getDailyData();
    if (!data.inProgress) return null;
    return {
      answers: data.savedAnswers || [],
      currentQuestion: data.savedQuestion || 0,
      score: data.savedScore || 0,
    };
  } catch {
    return null;
  }
}

export function clearDailyProgress() {
  try {
    const data = getDailyData();
    const { inProgress, savedAnswers, savedQuestion, savedScore, ...clean } = data;
    localStorage.setItem(DAILY_KEY, JSON.stringify(clean));
  } catch {}
}


// Fire-and-forget per-play log for the profile Scouting Report (daily_results
// table). Mirrors logNTGSrResult: logging must NEVER break a game save. The
// RPC clips strings and caps the array server-side, and the unique
// (user, day, sport) key turns any double-fire into a no-op.
function logDailyResult({ userId, sport, score, passed, answers, source = 'play' }) {
  if (!userId) return;
  try {
    supabase.rpc('log_daily_result', {
      p_user_id: userId,
      p_sport: sport,
      p_score: score,
      p_passed: passed,
      p_answers: (answers || []).map(a => ({ player: a.player, college: a.college, answer: a.answer, isCorrect: !!a.isCorrect })),
      p_source: source,
    }).then(({ error }) => { if (error) console.error('logDailyResult error:', error); })
      .catch((err) => console.error('logDailyResult exception:', err));
  } catch (err) {
    console.error('logDailyResult threw:', err);
  }
}

export async function saveDailyResult(score, currentUser, fireIronman = true, answers = []) {
  const today = getTodaysDate();
  const passed = score >= PASS_THRESHOLD;
  const data = getDailyData();

  if (data.lastPlayedDate === today) return { ...data, passed };

  // Optimistic local update so UI reflects the play immediately
  // lastAnswers rides along so a guest who signs up later claims the full
  // detail, not just the score. Cleared by the final/claim rewrites.
  const optimistic = { streak: data.streak || 0, lastPlayedDate: today, lastScore: score, lastAnswers: answers };
  try { localStorage.setItem(DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    logDailyResult({ userId: currentUser.id, sport: 'nfl', score, passed, answers });
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_nfl_daily_streak', {
        p_user_id: currentUser.id,
        p_passed: passed,
      });
      if (error) {
        console.error('saveDailyResult RPC error:', error);
        return { ...optimistic, passed };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, lastPlayedDate: today, lastScore: score };
        try { localStorage.setItem(DAILY_KEY, JSON.stringify(final)); } catch {}
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (nfl):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (nfl):', imEx);
          }
        }
        return { ...final, passed, ironmanStreak };
      }
    } catch (err) {
      console.error('saveDailyResult Supabase error:', err);
    }
  }

  return { ...optimistic, passed };
}

// Call this after a guest signs up or signs in, saves today's result if they played as a guest
export const claimGuestDailyResult = withClaimLatch(DAILY_KEY, async function (user) {
  const data = getDailyData();
  const today = getTodaysDate();
  if (data.lastPlayedDate !== today || data.lastScore === null) return;
  if (alreadyClaimed(data)) return; // per-browser result: claim exactly once

  const passed = data.lastScore >= PASS_THRESHOLD;

  let newStreak;
  try {
    const { data: rpcData, error } = await supabase.rpc('apply_nfl_daily_streak', {
      p_user_id: user.id,
      p_passed: passed,
    });
    if (error) { console.error('Failed to claim guest daily result:', error); }
    else {
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
      logDailyResult({ userId: user.id, sport: 'nfl', score: data.lastScore, passed, answers: data.lastAnswers || [], source: 'guest_claim' });
        newStreak = row.new_streak;
        markClaimed(DAILY_KEY, data, user.id, { streak: row.new_streak });
      }
    }
  } catch (err) {
    console.error('claimGuestDailyResult error:', err);
  }

  // LIDS credits on any daily completion — fire regardless of sport RPC result
  let ironmanStreak;
  try {
    const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: user.id });
    if (imErr) console.error('claimGuestDailyResult ironman error:', imErr);
    else {
      const imRow = Array.isArray(imData) ? imData[0] : imData;
      ironmanStreak = imRow?.new_streak;
    }
  } catch (imEx) {
    console.error('claimGuestDailyResult ironman exception:', imEx);
  }

  return { newStreak, ironmanStreak };
});

export const claimGuestNBADailyResult = withClaimLatch(NBA_DAILY_KEY, async function (user) {
  const data = getNBADailyData();
  const today = getTodaysDate();
  if (data.lastPlayedDate !== today || data.lastScore === null) return;

  if (alreadyClaimed(data)) return; // per-browser result: claim exactly once

  const passed = data.lastScore >= PASS_THRESHOLD;
  try {
    const { data: rpcData, error } = await supabase.rpc('apply_nba_daily_streak', {
      p_user_id: user.id,
      p_passed: passed,
    });
    if (error) { console.error('Failed to claim guest NBA daily result:', error); return; }
    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (row?.new_streak !== undefined) {
      logDailyResult({ userId: user.id, sport: 'nba', score: data.lastScore, passed, answers: data.lastAnswers || [], source: 'guest_claim' });
      markClaimed(NBA_DAILY_KEY, data, user.id, { streak: row.new_streak });
      return { newStreak: row.new_streak };
    }
  } catch (err) {
    console.error('claimGuestNBADailyResult error:', err);
  }
});

export const claimGuestMoshDailyResult = withClaimLatch(MOSH_DAILY_KEY, async function (user) {
  const data = getMoshDailyData();
  const today = getTodaysDate();
  if (data.lastPlayedDate !== today || data.lastScore === null) return;

  if (alreadyClaimed(data)) return; // per-browser result: claim exactly once

  const passed = data.lastScore >= PASS_THRESHOLD;
  try {
    const { data: rpcData, error } = await supabase.rpc('apply_mosh_daily_streak', {
      p_user_id: user.id,
      p_passed: passed,
    });
    if (error) { console.error('Failed to claim guest Mosh daily result:', error); return; }
    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (row?.new_streak !== undefined) {
      logDailyResult({ userId: user.id, sport: 'mosh', score: data.lastScore, passed, answers: data.lastAnswers || [], source: 'guest_claim' });
      markClaimed(MOSH_DAILY_KEY, data, user.id, { streak: row.new_streak });
      return { newStreak: row.new_streak };
    }
  } catch (err) {
    console.error('claimGuestMoshDailyResult error:', err);
  }
});

// ───────────────────────────────────────────────────────────────────
// NBA DAILY
// ───────────────────────────────────────────────────────────────────

export function getNBADailyData() {
  try {
    const raw = localStorage.getItem(NBA_DAILY_KEY);
    if (!raw) return { streak: 0, lastPlayedDate: null, lastScore: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, lastPlayedDate: null, lastScore: null };
  }
}

export function hasPlayedNBAToday() {
  return getNBADailyData().lastPlayedDate === getTodaysDate();
}

export function saveNBADailyProgress(answers, currentQuestion, score) {
  try {
    const data = getNBADailyData();
    localStorage.setItem(NBA_DAILY_KEY, JSON.stringify({
      ...data,
      inProgress: true,
      savedAnswers: answers,
      savedQuestion: currentQuestion,
      savedScore: score,
    }));
  } catch {}
}

export function getNBADailyProgress() {
  try {
    const data = getNBADailyData();
    if (!data.inProgress) return null;
    return {
      answers: data.savedAnswers || [],
      currentQuestion: data.savedQuestion || 0,
      score: data.savedScore || 0,
    };
  } catch {
    return null;
  }
}

export function clearNBADailyProgress() {
  try {
    const data = getNBADailyData();
    const { inProgress, savedAnswers, savedQuestion, savedScore, ...clean } = data;
    localStorage.setItem(NBA_DAILY_KEY, JSON.stringify(clean));
  } catch {}
}

export async function saveNBADailyResult(score, currentUser, fireIronman = true, answers = []) {
  const today = getTodaysDate();
  const passed = score >= PASS_THRESHOLD;
  const data = getNBADailyData();

  if (data.lastPlayedDate === today) return { ...data, passed };

  // lastAnswers rides along so a guest who signs up later claims the full
  // detail, not just the score. Cleared by the final/claim rewrites.
  const optimistic = { streak: data.streak || 0, lastPlayedDate: today, lastScore: score, lastAnswers: answers };
  try { localStorage.setItem(NBA_DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    logDailyResult({ userId: currentUser.id, sport: 'nba', score, passed, answers });
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_nba_daily_streak', {
        p_user_id: currentUser.id,
        p_passed: passed,
      });
      if (error) {
        console.error('saveNBADailyResult RPC error:', error);
        return { ...optimistic, passed };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, lastPlayedDate: today, lastScore: score };
        try { localStorage.setItem(NBA_DAILY_KEY, JSON.stringify(final)); } catch {}
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (nba):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (nba):', imEx);
          }
        }
        return { ...final, passed, ironmanStreak };
      }
    } catch (err) {
      console.error('saveNBADailyResult Supabase error:', err);
    }
  }

  return { ...optimistic, passed };
}

// ───────────────────────────────────────────────────────────────────
// MISH MOSH DAILY
// ───────────────────────────────────────────────────────────────────

export function getMoshDailyData() {
  try {
    const raw = localStorage.getItem(MOSH_DAILY_KEY);
    if (!raw) return { streak: 0, lastPlayedDate: null, lastScore: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, lastPlayedDate: null, lastScore: null };
  }
}

export function hasPlayedMoshToday() {
  return getMoshDailyData().lastPlayedDate === getTodaysDate();
}

export function saveMoshDailyProgress(answers, currentQuestion, score) {
  try {
    const data = getMoshDailyData();
    localStorage.setItem(MOSH_DAILY_KEY, JSON.stringify({
      ...data,
      inProgress: true,
      savedAnswers: answers,
      savedQuestion: currentQuestion,
      savedScore: score,
    }));
  } catch {}
}

export function getMoshDailyProgress() {
  try {
    const data = getMoshDailyData();
    if (!data.inProgress) return null;
    return {
      answers: data.savedAnswers || [],
      currentQuestion: data.savedQuestion || 0,
      score: data.savedScore || 0,
    };
  } catch {
    return null;
  }
}

export function clearMoshDailyProgress() {
  try {
    const data = getMoshDailyData();
    const { inProgress, savedAnswers, savedQuestion, savedScore, ...clean } = data;
    localStorage.setItem(MOSH_DAILY_KEY, JSON.stringify(clean));
  } catch {}
}

export async function saveMoshDailyResult(score, currentUser, fireIronman = true, answers = []) {
  const today = getTodaysDate();
  const passed = score >= PASS_THRESHOLD;
  const data = getMoshDailyData();

  if (data.lastPlayedDate === today) return { ...data, passed };

  // lastAnswers rides along so a guest who signs up later claims the full
  // detail, not just the score. Cleared by the final/claim rewrites.
  const optimistic = { streak: data.streak || 0, lastPlayedDate: today, lastScore: score, lastAnswers: answers };
  try { localStorage.setItem(MOSH_DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    logDailyResult({ userId: currentUser.id, sport: 'mosh', score, passed, answers });
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_mosh_daily_streak', {
        p_user_id: currentUser.id,
        p_passed: passed,
      });
      if (error) {
        console.error('saveMoshDailyResult RPC error:', error);
        return { ...optimistic, passed };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, lastPlayedDate: today, lastScore: score };
        try { localStorage.setItem(MOSH_DAILY_KEY, JSON.stringify(final)); } catch {}
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (mosh):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (mosh):', imEx);
          }
        }
        return { ...final, passed, ironmanStreak };
      }
    } catch (err) {
      console.error('saveMoshDailyResult Supabase error:', err);
    }
  }

  return { ...optimistic, passed };
}

// ───────────────────────────────────────────────────────────────────
// NAME THAT GUY (NTG)
// ───────────────────────────────────────────────────────────────────

// Read-only fetch (Option A). All same-day players get the same question:
// 1. A row explicitly scheduled for today (Chicago).
// 2. Else a row already stamped used_on today (the day's question, once stamped).
// 3. Else the next active, unused row by queue_order.
export async function fetchTodaysNTGQuestion() {
  const today = getChicagoDate();
  const cols = 'id, question, question_note, answer_name, wrong_1, wrong_2, wrong_3, sport, photo_url';
  try {
    const { data: scheduled, error: schedErr } = await supabase
      .from('ntg_questions')
      .select(cols)
      .eq('active', true)
      .eq('scheduled_date', today)
      .limit(1);
    if (schedErr) console.error('fetchTodaysNTGQuestion scheduled error:', schedErr);
    if (scheduled && scheduled.length > 0) return scheduled[0];

    const { data: usedToday, error: usedErr } = await supabase
      .from('ntg_questions')
      .select(cols)
      .eq('active', true)
      .eq('used_on', today)
      .order('queue_order', { ascending: true, nullsFirst: false })
      .limit(1);
    if (usedErr) console.error('fetchTodaysNTGQuestion usedToday error:', usedErr);
    if (usedToday && usedToday.length > 0) return usedToday[0];

    const { data: queued, error: queueErr } = await supabase
      .from('ntg_questions')
      .select(cols)
      .eq('active', true)
      .is('used_on', null)
      .order('queue_order', { ascending: true, nullsFirst: false })
      .limit(1);
    if (queueErr) { console.error('fetchTodaysNTGQuestion queue error:', queueErr); return null; }
    return (queued && queued.length > 0) ? queued[0] : null;
  } catch (err) {
    console.error('fetchTodaysNTGQuestion exception:', err);
    return null;
  }
}

// Mark today's question as used (Chicago). RLS blocks client UPDATEs on
// ntg_questions, so this goes through a SECURITY DEFINER RPC.
export async function stampNTGQuestionUsed(questionId) {
  if (!questionId) return;
  try {
    const { error } = await supabase.rpc('stamp_ntg_used', { p_question_id: questionId });
    if (error) console.error('stampNTGQuestionUsed error:', error);
  } catch (err) {
    console.error('stampNTGQuestionUsed exception:', err);
  }
}

export function getNTGDailyData() {
  try {
    const raw = localStorage.getItem(NTG_DAILY_KEY);
    if (!raw) return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null };
  }
}

export function hasPlayedNTGToday() {
  return getNTGDailyData().lastPlayedDate === getTodaysDate();
}

export async function saveNTGDailyResult(passed, currentUser, fireIronman = true) {
  const today = getTodaysDate();
  const score = passed ? 1 : 0;
  const data = getNTGDailyData();

  if (data.lastPlayedDate === today) return { ...data, passed };

  // Carry prior strikes only. Never guess the outcome (a loss may be a save OR a
  // strikeout) — the server's new_strikes is the source of truth and corrects this below.
  const optimistic = { streak: data.streak || 0, strikes: data.strikes || 0, lastPlayedDate: today, lastScore: score };
  try { localStorage.setItem(NTG_DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_ntg_streak', {
        p_user_id: currentUser.id,
        p_passed: passed,
      });
      if (error) {
        console.error('saveNTGDailyResult RPC error:', error);
        return { ...optimistic, passed };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, strikes: row.new_strikes, lastPlayedDate: today, lastScore: score };
        try { localStorage.setItem(NTG_DAILY_KEY, JSON.stringify(final)); } catch {}
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (ntg):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (ntg):', imEx);
          }
        }
        return { ...final, passed, ironmanStreak };
      }
    } catch (err) {
      console.error('saveNTGDailyResult Supabase error:', err);
    }
  }

  return { ...optimistic, passed };
}

export const claimGuestNTGDailyResult = withClaimLatch(NTG_DAILY_KEY, async function (user) {
  const data = getNTGDailyData();
  const today = getTodaysDate();
  if (data.lastPlayedDate !== today || data.lastScore === null) return;

  if (alreadyClaimed(data)) return; // per-browser result: claim exactly once

  const passed = data.lastScore >= NTG_PASS_THRESHOLD;

  let newStreak;
  let newStrikes;
  try {
    const { data: rpcData, error } = await supabase.rpc('apply_ntg_streak', {
      p_user_id: user.id,
      p_passed: passed,
    });
    if (error) { console.error('Failed to claim guest NTG daily result:', error); }
    else {
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        newStreak = row.new_streak;
        newStrikes = row.new_strikes;
        markClaimed(NTG_DAILY_KEY, data, user.id, { streak: row.new_streak, strikes: row.new_strikes });
      }
    }
  } catch (err) {
    console.error('claimGuestNTGDailyResult error:', err);
  }

  // LIDS credits on any daily completion — fire regardless of sport RPC result
  let ironmanStreak;
  try {
    const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: user.id });
    if (imErr) console.error('claimGuestNTGDailyResult ironman error:', imErr);
    else {
      const imRow = Array.isArray(imData) ? imData[0] : imData;
      ironmanStreak = imRow?.new_streak;
    }
  } catch (imEx) {
    console.error('claimGuestNTGDailyResult ironman exception:', imEx);
  }

  return { newStreak, newStrikes, ironmanStreak };
});

// ── NTG SR. (Name That Guy Sr. — opener/hints/free-text daily) ──────────────
// Everything below is a structural clone of the Jr. functions above; only the
// storage key, table/RPC names, and the persisted hintsUsed field differ.
// hintsUsed is stored so the DAILY_LOCKED share can rebuild the player's
// actual performance tier later (the Jr. bug was a generic string here).

const NTG_SR_DAILY_KEY = 'kyg_ntg_sr';

// Served by a SECURITY DEFINER RPC (today-or-earlier RLS hides the future
// queue from anon, so the 3-tier fallback runs server-side).
export async function fetchTodaysNTGSrPuzzle() {
  try {
    const { data, error } = await supabase.rpc('fetch_todays_ntg_sr_puzzle');
    if (error) { console.error('fetchTodaysNTGSrPuzzle error:', error); return null; }
    return (data && data.length > 0) ? data[0] : null;
  } catch (err) {
    console.error('fetchTodaysNTGSrPuzzle exception:', err);
    return null;
  }
}

// Audit log for Sr. results. Fire-and-forget and fully swallowed: this exists
// to make results auditable, and must never add latency to a save or surface
// an error to a player. Records which code path wrote the result ('play' vs
// 'guest_claim') so a disputed streak can be traced instead of inferred.
function logNTGSrResult({ userId, won, hintsUsed, puzzleId, source, streakAfter, strikesAfter }) {
  if (!userId) return;
  try {
    supabase.rpc('log_ntg_sr_result', {
      p_user_id: userId,
      p_won: won,
      p_hints_used: hintsUsed ?? null,
      p_puzzle_id: puzzleId ?? null,
      p_source: source,
      p_streak_after: streakAfter ?? null,
      p_strikes_after: strikesAfter ?? null,
    }).then(({ error }) => { if (error) console.error('logNTGSrResult error:', error); })
      .catch((err) => console.error('logNTGSrResult exception:', err));
  } catch (err) {
    console.error('logNTGSrResult threw:', err);
  }
}

export async function stampNTGSrPuzzleUsed(puzzleId) {
  if (!puzzleId) return;
  try {
    const { error } = await supabase.rpc('stamp_ntg_sr_used', { p_puzzle_id: puzzleId });
    if (error) console.error('stampNTGSrPuzzleUsed error:', error);
  } catch (err) {
    console.error('stampNTGSrPuzzleUsed exception:', err);
  }
}

export function getNTGSrDailyData() {
  try {
    const raw = localStorage.getItem(NTG_SR_DAILY_KEY);
    if (!raw) return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null, hintsUsed: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null, hintsUsed: null };
  }
}

export function hasPlayedNTGSrToday() {
  return getNTGSrDailyData().lastPlayedDate === getTodaysDate();
}

// Mid-game persistence for Sr., mirroring saveDailyProgress/getDailyProgress.
// Sr. is one puzzle with three guesses, so the only thing worth carrying is the
// list of wrong guesses (which also drives how many hints are revealed).
// Saved on every miss rather than only on exit, so a tab close, a refresh or the
// resume-reload all survive - not just a deliberate HOME tap.
export function saveNTGSrProgress(wrongGuesses, puzzleId) {
  try {
    const data = getNTGSrDailyData();
    localStorage.setItem(NTG_SR_DAILY_KEY, JSON.stringify({
      ...data,
      inProgress: true,
      savedGuesses: wrongGuesses,
      savedPuzzleId: puzzleId || null,
      savedDate: getTodaysDate(),
    }));
  } catch {}
}

export function getNTGSrProgress(puzzleId) {
  try {
    const data = getNTGSrDailyData();
    if (!data.inProgress) return null;
    // Same Pacific day only, else tomorrow's puzzle would inherit today's misses.
    if (data.savedDate !== getTodaysDate()) return null;
    // Same puzzle only. If the queue row is swapped mid-day (it has been), stale
    // guesses must not carry into a different guy.
    if (puzzleId && data.savedPuzzleId && data.savedPuzzleId !== puzzleId) return null;
    const guesses = Array.isArray(data.savedGuesses) ? data.savedGuesses : [];
    return guesses.length ? { wrongGuesses: guesses } : null;
  } catch {
    return null;
  }
}

export function clearNTGSrProgress() {
  try {
    const data = getNTGSrDailyData();
    const { inProgress, savedGuesses, savedPuzzleId, savedDate, savedExits, ...clean } = data;
    localStorage.setItem(NTG_SR_DAILY_KEY, JSON.stringify(clean));
  } catch {}
}

// Exit counter for Sr.'s leave-confirm. Deliberately on the kyg_ntg_sr key, NOT
// the shared kyg_daily one GameBoard uses - that counter is read and written on
// kyg_daily for all three dailies, so exiting NBA burns the NFL free exit. Sr.
// keeps its own, scoped to the same day + puzzle as the rest of its progress.
export function getNTGSrExits(puzzleId) {
  try {
    const data = getNTGSrDailyData();
    if (data.savedDate !== getTodaysDate()) return 0;
    if (puzzleId && data.savedPuzzleId && data.savedPuzzleId !== puzzleId) return 0;
    return data.savedExits || 0;
  } catch {
    return 0;
  }
}

export function bumpNTGSrExits(puzzleId) {
  try {
    const data = getNTGSrDailyData();
    const sameDay = data.savedDate === getTodaysDate();
    const samePuzzle = !puzzleId || !data.savedPuzzleId || data.savedPuzzleId === puzzleId;
    const prior = sameDay && samePuzzle ? data.savedExits || 0 : 0;
    localStorage.setItem(NTG_SR_DAILY_KEY, JSON.stringify({
      ...data,
      savedExits: prior + 1,
      savedPuzzleId: puzzleId || data.savedPuzzleId || null,
      savedDate: getTodaysDate(),
    }));
  } catch {}
}

export async function saveNTGSrDailyResult(won, hintsUsed, currentUser, puzzleId = null, fireIronman = true) {
  const today = getTodaysDate();
  const score = won ? 1 : 0;
  const data = getNTGSrDailyData();

  if (data.lastPlayedDate === today) return { ...data, passed: won };

  // Carry prior strikes only. Never guess the outcome (a loss may be a save OR a
  // strikeout) — the server's new_strikes is the source of truth and corrects this below.
  const optimistic = { streak: data.streak || 0, strikes: data.strikes || 0, lastPlayedDate: today, lastScore: score, hintsUsed };
  try { localStorage.setItem(NTG_SR_DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_ntg_sr_streak', {
        p_user_id: currentUser.id,
        p_passed: won,
      });
      if (error) {
        console.error('saveNTGSrDailyResult RPC error:', error);
        return { ...optimistic, passed: won };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, strikes: row.new_strikes, lastPlayedDate: today, lastScore: score, hintsUsed };
        try { localStorage.setItem(NTG_SR_DAILY_KEY, JSON.stringify(final)); } catch {}
        logNTGSrResult({ userId: currentUser.id, won, hintsUsed, puzzleId, source: 'play', streakAfter: row.new_streak, strikesAfter: row.new_strikes });
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (ntg_sr):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (ntg_sr):', imEx);
          }
        }
        return { ...final, passed: won, ironmanStreak };
      }
    } catch (err) {
      console.error('saveNTGSrDailyResult Supabase error:', err);
    }
  }

  return { ...optimistic, passed: won };
}

export const claimGuestNTGSrDailyResult = withClaimLatch(NTG_SR_DAILY_KEY, async function (user) {
  const data = getNTGSrDailyData();
  const today = getTodaysDate();
  if (data.lastPlayedDate !== today || data.lastScore === null) return;

  if (alreadyClaimed(data)) return; // per-browser result: claim exactly once

  const passed = data.lastScore >= 1;

  let newStreak;
  let newStrikes;
  try {
    const { data: rpcData, error } = await supabase.rpc('apply_ntg_sr_streak', {
      p_user_id: user.id,
      p_passed: passed,
    });
    if (error) { console.error('Failed to claim guest NTG Sr. daily result:', error); }
    else {
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        newStreak = row.new_streak;
        newStrikes = row.new_strikes;
        markClaimed(NTG_SR_DAILY_KEY, data, user.id, { streak: row.new_streak, strikes: row.new_strikes });
        // Logged separately from 'play': this path replays whatever result is
        // sitting in THIS BROWSER's localStorage onto the account that just
        // signed in. A guest_claim row landing on an account that also has a
        // play row for the same day is the overwrite signal to look for.
        logNTGSrResult({ userId: user.id, won: passed, hintsUsed: data.hintsUsed, puzzleId: null, source: 'guest_claim', streakAfter: row.new_streak, strikesAfter: row.new_strikes });
      }
    }
  } catch (err) {
    console.error('claimGuestNTGSrDailyResult error:', err);
  }

  // LIDS credits on any daily completion — fire regardless of sport RPC result
  let ironmanStreak;
  try {
    const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: user.id });
    if (imErr) console.error('claimGuestNTGSrDailyResult ironman error:', imErr);
    else {
      const imRow = Array.isArray(imData) ? imData[0] : imData;
      ironmanStreak = imRow?.new_streak;
    }
  } catch (imEx) {
    console.error('claimGuestNTGSrDailyResult ironman exception:', imEx);
  }

  return { newStreak, newStrikes, ironmanStreak };
});

// ── DIGITS (cross-sport jersey-number daily) ──────────────────────────
// Locked game: account required, so there is no guest-claim path. Same
// queue/streak/strike mechanics as NTG Jr., pointed at digits_questions.
const DIGITS_DAILY_KEY = 'kyg_digits';

export async function fetchTodaysDigitsQuestion() {
  const today = getChicagoDate();
  const cols = 'id, number, question, question_note, answer_name, wrong_1, wrong_2, wrong_3, answer_sport, photo_url';
  try {
    const { data: scheduled, error: schedErr } = await supabase
      .from('digits_questions')
      .select(cols)
      .eq('active', true)
      .eq('scheduled_date', today)
      .limit(1);
    if (schedErr) console.error('fetchTodaysDigitsQuestion scheduled error:', schedErr);
    if (scheduled && scheduled.length > 0) return scheduled[0];

    const { data: usedToday, error: usedErr } = await supabase
      .from('digits_questions')
      .select(cols)
      .eq('active', true)
      .eq('used_on', today)
      .order('queue_order', { ascending: true, nullsFirst: false })
      .limit(1);
    if (usedErr) console.error('fetchTodaysDigitsQuestion usedToday error:', usedErr);
    if (usedToday && usedToday.length > 0) return usedToday[0];

    const { data: queued, error: queueErr } = await supabase
      .from('digits_questions')
      .select(cols)
      .eq('active', true)
      .is('used_on', null)
      .order('queue_order', { ascending: true, nullsFirst: false })
      .limit(1);
    if (queueErr) { console.error('fetchTodaysDigitsQuestion queue error:', queueErr); return null; }
    return (queued && queued.length > 0) ? queued[0] : null;
  } catch (err) {
    console.error('fetchTodaysDigitsQuestion exception:', err);
    return null;
  }
}

export async function stampDigitsQuestionUsed(questionId) {
  if (!questionId) return;
  try {
    const { error } = await supabase.rpc('stamp_digits_used', { p_question_id: questionId });
    if (error) console.error('stampDigitsQuestionUsed error:', error);
  } catch (err) {
    console.error('stampDigitsQuestionUsed exception:', err);
  }
}

export function getDigitsDailyData() {
  try {
    const raw = localStorage.getItem(DIGITS_DAILY_KEY);
    if (!raw) return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null };
    return JSON.parse(raw);
  } catch {
    return { streak: 0, strikes: 0, lastPlayedDate: null, lastScore: null };
  }
}

export function hasPlayedDigitsToday() {
  return getDigitsDailyData().lastPlayedDate === getTodaysDate();
}

export async function saveDigitsDailyResult(passed, currentUser, fireIronman = true) {
  const today = getTodaysDate();
  const score = passed ? 1 : 0;
  const data = getDigitsDailyData();
  if (data.lastPlayedDate === today) return { ...data, passed };

  // Carry prior strikes only. Never guess the outcome (a loss may be a save OR
  // a strikeout) - the server's new_strikes is the source of truth below.
  const optimistic = { streak: data.streak || 0, strikes: data.strikes || 0, lastPlayedDate: today, lastScore: score };
  try { localStorage.setItem(DIGITS_DAILY_KEY, JSON.stringify(optimistic)); } catch {}

  if (currentUser?.id) {
    try {
      const { data: rpcData, error } = await supabase.rpc('apply_digits_streak', {
        p_user_id: currentUser.id,
        p_passed: passed,
      });
      if (error) {
        console.error('saveDigitsDailyResult RPC error:', error);
        return { ...optimistic, passed };
      }
      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (row?.new_streak !== undefined) {
        const final = { streak: row.new_streak, strikes: row.new_strikes, lastPlayedDate: today, lastScore: score };
        try { localStorage.setItem(DIGITS_DAILY_KEY, JSON.stringify(final)); } catch {}
        let ironmanStreak;
        if (fireIronman) {
          try {
            const { data: imData, error: imErr } = await supabase.rpc('apply_ironman_streak', { p_user_id: currentUser.id });
            if (imErr) console.error('apply_ironman_streak error (digits):', imErr);
            else {
              const imRow = Array.isArray(imData) ? imData[0] : imData;
              ironmanStreak = imRow?.new_streak;
            }
          } catch (imEx) {
            console.error('apply_ironman_streak exception (digits):', imEx);
          }
        }
        return { ...final, passed, ironmanStreak };
      }
    } catch (err) {
      console.error('saveDigitsDailyResult Supabase error:', err);
    }
  }
  return { ...optimistic, passed };
}
